import type { CalendarDate, CalendarDateTime } from '@internationalized/date';
import type { DateValue } from 'react-aria-components';
import { toCalendarDate } from './dateUtils';

export interface TimeComponents {
  hour: number;
  minute: number;
  second: number;
}

/** Extract CalendarDateTime constraints from DateValue bounds, relative to a specific date. */
export function getTimeBoundsForDate(
  date: CalendarDate,
  minValue: DateValue | undefined,
  maxValue: DateValue | undefined
): { minTime: TimeComponents | null; maxTime: TimeComponents | null } {
  const minDt = minValue && 'hour' in minValue ? (minValue as CalendarDateTime) : null;
  const maxDt = maxValue && 'hour' in maxValue ? (maxValue as CalendarDateTime) : null;
  const isMinDay = minDt && date.compare(toCalendarDate(minDt)) === 0;
  const isMaxDay = maxDt && date.compare(toCalendarDate(maxDt)) === 0;
  return {
    minTime: isMinDay ? { hour: minDt!.hour, minute: minDt!.minute, second: minDt!.second } : null,
    maxTime: isMaxDay ? { hour: maxDt!.hour, minute: maxDt!.minute, second: maxDt!.second } : null,
  };
}

/** Clamp h/m/s to the allowed time bounds for a given date. Returns the (possibly unchanged) time. */
export function clampTime(
  date: CalendarDate,
  hour: number,
  minute: number,
  second: number,
  minValue: DateValue | undefined,
  maxValue: DateValue | undefined
): TimeComponents {
  const { minTime, maxTime } = getTimeBoundsForDate(date, minValue, maxValue);
  let h = hour,
    m = minute,
    s = second;

  if (minTime) {
    if (h < minTime.hour) {
      h = minTime.hour;
      m = minTime.minute;
      s = minTime.second;
    } else if (h === minTime.hour && m < minTime.minute) {
      m = minTime.minute;
      s = minTime.second;
    } else if (h === minTime.hour && m === minTime.minute && s < minTime.second) {
      s = minTime.second;
    }
  }

  if (maxTime) {
    if (h > maxTime.hour) {
      h = maxTime.hour;
      m = maxTime.minute;
      s = maxTime.second;
    } else if (h === maxTime.hour && m > maxTime.minute) {
      m = maxTime.minute;
      s = maxTime.second;
    } else if (h === maxTime.hour && m === maxTime.minute && s > maxTime.second) {
      s = maxTime.second;
    }
  }

  return { hour: h, minute: m, second: s };
}

/**
 * Returns the current wall-clock time as h24 components, clamped to the allowed bounds
 * for the given date. If no date is provided, returns the raw current time unclamped.
 */
export function currentTimeClampedToRange(
  forDate: CalendarDate | undefined,
  minValue: DateValue | undefined,
  maxValue: DateValue | undefined
): TimeComponents {
  const now = new Date();
  const current = { hour: now.getHours(), minute: now.getMinutes(), second: now.getSeconds() };
  if (!forDate) return current;
  return clampTime(forDate, current.hour, current.minute, current.second, minValue, maxValue);
}

/**
 * Produce a seed date (today, clamped to min/max bounds).
 * Returns TimeComponents shape reused for consistency; callers use year/month/day from CalendarDate.
 */
export function clampDateToRange(
  date: CalendarDate,
  minCal: CalendarDate | null,
  maxCal: CalendarDate | null
): CalendarDate {
  if (minCal && date.compare(minCal) < 0) return minCal;
  if (maxCal && date.compare(maxCal) > 0) return maxCal;
  return date;
}
